/**
 * Global Dead Bright behaviour, loaded on every page:
 * - arms the accent crossfade after first paint
 * - clears the hero ignition flag once the one-second ramp is over
 * - pauses the fog while the tab is hidden
 * - keeps date-driven text (order-by bar, countdown, delivery estimate) in step with fixed dates
 * - reports db_checkout_click when the checkout button is pressed (cart drawer or cart page)
 *
 * Markup contract (all server-rendered, so the page reads correctly without this file):
 *   [data-db-when="before|after"]   shown only before / after the order-by moment
 *   [data-db-countdown]             contains [data-unit="days|hours|minutes"] children
 *   [data-db-delivery]              text replaced with the live "Order today, arrives ..." sentence
 *   [data-db-date-offset="N"]       text replaced with the date N days from now
 *                                   (add data-db-date-format="day-month" for "9 Oct")
 */
import { isPastCutoff, msUntilCutoff, countdownParts, deliverySentence } from '@theme/db-time';
import { emit } from '@theme/db-events';

requestAnimationFrame(() => document.documentElement.classList.add('db-accent-ready'));

// The hero ignition (CSS in sections/db-hero.liquid, keyed on this flag) lasts about a second. Clearing the flag
// afterwards keeps a hero that the theme editor re-renders from lighting up a second time.
if (document.documentElement.hasAttribute('data-db-ignite')) {
  window.setTimeout(() => document.documentElement.removeAttribute('data-db-ignite'), 1600);
}

// <html> cannot see the header heights that the header scripts set on <body>; copy the sticky header's height so
// anchor jumps clear it when the window is the scroller (see scroll-padding-top in db-base.css).
{
  const copyHeaderHeight = () => {
    const height = document.body.style.getPropertyValue('--header-height');
    if (height) document.documentElement.style.setProperty('--db-header-h', height);
  };
  copyHeaderHeight();
  new MutationObserver(copyHeaderHeight).observe(document.body, { attributes: true, attributeFilter: ['style'] });
}

document.addEventListener('visibilitychange', () => {
  document.documentElement.classList.toggle('db-fog-paused', document.hidden);
});

const DAY_MS = 86400000;

/** Day-first dates everywhere except the US ("9 Oct", not "Oct 9"). */
function dateLocale() {
  const lang = window.DB?.locale || 'en';
  if (lang !== 'en') return lang;
  return window.Shopify?.country === 'US' ? 'en-US' : 'en-GB';
}
const pad = (n) => String(n).padStart(2, '0');

function renderCountdown(el, parts) {
  for (const unit of ['days', 'hours', 'minutes']) {
    const node = el.querySelector(`[data-unit="${unit}"]`);
    if (!node) continue;
    const next = unit === 'days' ? String(parts[unit]) : pad(parts[unit]);
    if (node.textContent !== next) node.textContent = next;
  }
}

function updateDates() {
  const nodes = document.querySelectorAll('[data-db-date-offset]');
  if (!nodes.length) return;
  const locale = dateLocale();
  const long = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' });
  const short = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  for (const el of nodes) {
    const format = el.getAttribute('data-db-date-format') === 'day-month' ? short : long;
    const next = format.format(new Date(Date.now() + Number(el.getAttribute('data-db-date-offset')) * DAY_MS));
    if (el.textContent !== next) el.textContent = next;
  }
}

function updateTime() {
  updateDates();
  const past = isPastCutoff();
  for (const el of document.querySelectorAll('[data-db-when]')) {
    const showWhen = el.getAttribute('data-db-when');
    el.hidden = showWhen === 'before' ? past : !past;
  }
  const parts = countdownParts(msUntilCutoff());
  for (const el of document.querySelectorAll('[data-db-countdown]')) renderCountdown(el, parts);
  const sentence = deliverySentence();
  if (sentence) {
    for (const el of document.querySelectorAll('[data-db-delivery]')) {
      if (el.textContent !== sentence) el.textContent = sentence;
    }
  }
}

function start() {
  updateTime();
  // once a minute is enough: the countdown shows days, hours and minutes
  setInterval(() => {
    if (document.visibilityState === 'visible') updateTime();
  }, 30000);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
else start();

document.addEventListener('shopify:section:load', updateTime);

// Checkout button (snippets/cart-summary.liquid prints the cart's total and mask count on it). One delegated listener
// covers the drawer and the cart page, survives section re-renders and runs before the page navigates away.
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[name="checkout"]') : null;
  if (!(target instanceof HTMLElement) || target.hasAttribute('disabled')) return;
  emit('db_checkout_click', {
    qty: Number(target.dataset.dbCount) || 0,
    value: Math.round(Number(target.dataset.dbTotal) || 0) / 100,
    location: target.closest('cart-drawer-component, #cart-drawer') ? 'cart_drawer' : 'cart_page',
  });
});
